import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { HeartHandshake, Loader2, Calendar, TrendingUp, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDate = (d) => d ? new Date(d + "T00:00").toLocaleDateString("pt-BR") : "—";

export const FilantropiaPublicPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/public/filantropia/${token}`, { withCredentials: false });
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Link inválido.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#080D18] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="min-h-screen bg-[#080D18] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-rose-500/15 text-rose-400 flex items-center justify-center mb-4">
            <ShieldCheck className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-serif font-bold text-white mb-2">Link indisponível</h1>
          <p className="text-slate-400">{err}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080D18] py-10 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center pb-6 border-b border-slate-800">
          {data.loja.logo_b64 && (
            <img
              src={data.loja.logo_b64.startsWith("data:") ? data.loja.logo_b64 : `data:image/png;base64,${data.loja.logo_b64}`}
              alt={data.loja.name}
              className="w-20 h-20 mx-auto rounded-2xl object-contain mb-4 bg-[#131C2E] p-2"
            />
          )}
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold">
            À Glória do Grande Arquiteto do Universo
          </div>
          <h1 className="text-3xl font-serif font-bold text-white mt-2">{data.loja.name}</h1>
          <p className="text-sm text-slate-400 mt-1">
            {data.loja.oriente && <>Oriente de {data.loja.oriente} • </>}nº {data.loja.number || "—"}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/40 text-purple-300 text-xs font-bold uppercase tracking-widest">
            <HeartHandshake className="w-4 h-4" />
            Prestação de Contas Pública • {data.bolsa.name}
          </div>
        </div>

        {/* Saldo grande */}
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-slate-400 mb-2">Saldo Atual do Fundo</p>
          <div className="text-5xl font-mono font-bold text-[#D4AF37]" data-testid="public-saldo">{fmt(data.bolsa.saldo_atual)}</div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Arrecadado" value={fmt(data.resumo.total_receita)} tone="emerald" />
          <KpiCard label="Distribuído" value={fmt(data.resumo.total_despesa)} tone="rose" />
          <KpiCard label="Auxílios" value={String(data.resumo.quantidade_auxilios)} tone="cyan" />
          <KpiCard label="Sessões c/ Tronco" value={String(data.resumo.quantidade_sessoes_tronco)} tone="purple" />
        </div>

        {/* Tronco por Sessão */}
        {data.tronco_por_sessao.length > 0 && (
          <div className="luxury-card p-5 bg-[#0B111E] border border-slate-800 rounded-2xl">
            <h2 className="font-serif font-bold text-white mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-purple-400" /> Arrecadação por Sessão
            </h2>
            <table className="w-full text-xs">
              <thead className="border-b border-slate-800 text-slate-400 uppercase tracking-wider">
                <tr><th className="text-left py-2">Data</th><th className="text-left">Sessão</th><th className="text-right">Tronco</th></tr>
              </thead>
              <tbody>
                {data.tronco_por_sessao.map((s, i) => (
                  <tr key={i} className="border-b border-slate-800/50">
                    <td className="py-2 text-slate-300">{fmtDate(s.date)}</td>
                    <td className="text-slate-300">nº {s.session_number || "—"}</td>
                    <td className="text-right font-mono font-bold text-purple-300">{fmt(s.trunk_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Movimentações anônimas */}
        <div className="luxury-card p-5 bg-[#0B111E] border border-slate-800 rounded-2xl">
          <h2 className="font-serif font-bold text-white mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" /> Histórico de Movimentações
          </h2>
          {data.movimentacoes_anonimas.length === 0 ? (
            <p className="text-xs text-slate-500 italic">Nenhuma movimentação registrada ainda.</p>
          ) : (
            <div className="space-y-2">
              {data.movimentacoes_anonimas.slice(0, 60).map((m, i) => (
                <div key={i} className="flex items-center justify-between text-xs p-2 rounded-lg border border-slate-800 bg-[#080C14]">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${m.type === "receita" ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
                      {m.type === "receita" ? "Entrada" : "Auxílio"}
                    </span>
                    <span className="text-slate-400">{fmtDate(m.date)}</span>
                    <span className="text-white truncate">{m.description}</span>
                  </div>
                  <span className={`font-mono font-bold shrink-0 ${m.type === "receita" ? "text-emerald-300" : "text-rose-300"}`}>
                    {m.type === "receita" ? "+" : "-"} {fmt(m.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[10px] text-slate-500 mt-3 italic">
            🔒 Beneficiários são exibidos anonimamente para preservar a privacidade dos Irmãos assistidos (LGPD).
          </p>
        </div>

        <div className="text-center text-[10px] text-slate-600 pt-6 border-t border-slate-800">
          Prestação de contas transparente • gerada pela plataforma <span className="text-[#D4AF37]">Lojas Integradas</span>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ label, value, tone }) => {
  const toneMap = {
    emerald: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
    rose: "text-rose-300 border-rose-500/30 bg-rose-500/5",
    cyan: "text-cyan-300 border-cyan-500/30 bg-cyan-500/5",
    purple: "text-purple-300 border-purple-500/30 bg-purple-500/5",
  };
  return (
    <div className={`rounded-xl border ${toneMap[tone]} p-3 text-center`}>
      <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold mb-1">{label}</div>
      <div className={`text-base font-mono font-bold ${toneMap[tone].split(" ")[0]}`}>{value}</div>
    </div>
  );
};

export default FilantropiaPublicPage;
